/**
 * JSON-model sealing wrappers (F6). Bridge between @beacon/crypto's byte-level
 * seal/open and the lexicon's sealedBroadcast/sealedNeedToKnow shapes. This
 * file owns the sealed-field JSON types (geo.ts imports SealedNeedToKnowJson).
 */
import {
  openBroadcast, openNeedToKnow, sealBroadcast, sealNeedToKnow,
  type NeedToKnowRecipient,
} from "@beacon/crypto";
import { stdB64Decode, stdB64Encode } from "./model.js";
import type { EpochKeys, KeyRegistry } from "./epoch-keys.js";
import type { IdentityKeys } from "./identity.js";

export interface SealedBroadcastJson { ciphertext: { $bytes: string }; keyRef: string }
export interface SealedNeedToKnowJson { ciphertext: { $bytes: string }; recipients: string[] }

export async function sealBroadcastJson(
  epochKeys: EpochKeys,
  nowMicros: number,
  pt: Uint8Array,
): Promise<SealedBroadcastJson> {
  const { keyRef, key } = await epochKeys.current(nowMicros);
  const ct = await sealBroadcast(key, keyRef, pt);
  return { ciphertext: { $bytes: stdB64Encode(ct) }, keyRef };
}

/** Seal under an explicit (keyRef, key) snapshot so multiple fields share one
 *  keyRef (E1 photos: report text + each photo component seal under the report's
 *  own epoch — shred the report, the photos die with it). */
export async function sealBroadcastWithKey(
  keyRef: string,
  key: Uint8Array,
  pt: Uint8Array,
): Promise<SealedBroadcastJson> {
  const ct = await sealBroadcast(key, keyRef, pt);
  return { ciphertext: { $bytes: stdB64Encode(ct) }, keyRef };
}

export async function openBroadcastJson(
  registry: KeyRegistry,
  sealed: SealedBroadcastJson,
): Promise<Uint8Array> {
  const key = await registry.get(sealed.keyRef);
  if (!key) throw new Error(`seal: unknown keyRef (shredded?): ${sealed.keyRef}`);
  return openBroadcast(key, sealed.keyRef, stdB64Decode(sealed.ciphertext.$bytes));
}

export async function sealNeedToKnowJson(
  recipients: NeedToKnowRecipient[],
  subjectTid: string,
  pt: Uint8Array,
): Promise<SealedNeedToKnowJson> {
  const ct = await sealNeedToKnow(recipients, subjectTid, pt);
  return { ciphertext: { $bytes: stdB64Encode(ct) }, recipients: recipients.map((r) => r.did) };
}

export async function openNeedToKnowJson(
  sealed: SealedNeedToKnowJson,
  subjectTid: string,
  me: Pick<IdentityKeys, "did" | "encryptionPrivate" | "encryptionKey">,
): Promise<Uint8Array> {
  return openNeedToKnow(
    stdB64Decode(sealed.ciphertext.$bytes),
    sealed.recipients,
    me.did,
    me.encryptionPrivate,
    me.encryptionKey,
    subjectTid,
  );
}
